import useQuizState from '@/hooks/useQuizState';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import QuizHeader from '@/components/quiz/QuizHeader';
import QuizNavigation from '@/components/quiz/QuizNavigation';
import Step1Location from '@/components/quiz/Step1Location';
import Step2Building from '@/components/quiz/Step2Building';
import Step3Insulation from '@/components/quiz/Step3Insulation';
import Step4Heating from '@/components/quiz/Step4Heating';
import Step5Goals from '@/components/quiz/Step5Goals';
import ConditionalQuestions from '@/components/quiz/ConditionalQuestions';

const STEP_KEYS = { 1: 'step1', 2: 'step2', 3: 'step3', 4: 'step4', 5: 'step5' };

export default function QuizPage() {
  const quiz = useQuizState();
  const { t } = useTranslation();
  const stepKey = STEP_KEYS[quiz.currentStep];

  const stepProps = {
    data: quiz.steps[stepKey],
    onUpdate: (updates) => quiz.updateStep(stepKey, updates),
    errors: quiz.errors,
  };

  return (
    <div className="min-h-screen bg-background py-12">
      <QuizHeader currentStep={quiz.currentStep} totalSteps={quiz.totalSteps} progress={quiz.progress} />

      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto bg-white rounded-xl p-8 shadow-sm border border-border" data-testid="quiz-container">
          {/* Calm confirmation after Step 1 → Step 2 transition */}
          {quiz.currentStep === 2 && (
            <div
              className="mb-5 -mt-2 flex items-start gap-2 bg-primary/5 border border-primary/20 rounded-lg px-3 py-2 text-sm text-primary/90"
              data-testid="step2-calm-confirmation"
            >
              <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t('quiz.step2Confirmation', 'Goed. We kijken nu naar uw woningtype, verbruik en beste eerste stap.')}</span>
            </div>
          )}

          {quiz.currentStep === 1 && (
            <>
              <Step1Location {...stepProps} onCountryChange={quiz.handleCountryChange} />
              <ConditionalQuestions
                isApartment={quiz.isApartment}
                isMonument={quiz.isMonument}
                isDetached={quiz.isDetached}
                apartmentDetails={quiz.apartmentDetails}
                setApartmentDetails={quiz.setApartmentDetails}
                monumentDetails={quiz.monumentDetails}
                setMonumentDetails={quiz.setMonumentDetails}
                detachedDetails={quiz.detachedDetails}
                setDetachedDetails={quiz.setDetachedDetails}
              />
            </>
          )}
          {quiz.currentStep === 2 && <Step2Building {...stepProps} />}
          {quiz.currentStep === 3 && <Step3Insulation {...stepProps} />}
          {quiz.currentStep === 4 && <Step4Heating {...stepProps} />}
          {quiz.currentStep === 5 && <Step5Goals {...stepProps} />}

          <QuizNavigation
            currentStep={quiz.currentStep}
            totalSteps={quiz.totalSteps}
            loading={quiz.loading}
            onBack={quiz.handleBack}
            onNext={quiz.handleNext}
            onSubmit={quiz.handleSubmit}
          />
        </div>
      </div>
    </div>
  );
}
